import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import {
  contentSchema,
  primaryCategorySchema,
  secondaryCategorySchema,
} from "./article-list";

const articleInfoSchema = z.object({
  articleId: z.string(),
  primaryCategory: primaryCategorySchema,
  secondaryCategory: secondaryCategorySchema,
  title: z.string().nullish(),
  slug: z.string().nullish(),
  coverPicture: z.string().nullish(),
  previewPicture: z.string().nullish(),
  description: z.string().nullish(),
  keywords: z.array(z.string()).nullish(),
  author: z.string().nullish(),
  content: contentSchema,
  permission: z.union([z.literal("Free"), z.literal("Subscribe")]),
  publishedAt: z.string().nullish(),
});

const responseSchema = z.object({
  data: articleInfoSchema,
  message: z.string(),
});

export type ArticleInfo = z.infer<typeof articleInfoSchema>;
export type ArticleInfoResponse = z.infer<typeof responseSchema>;

export const getArticleInfo = async ({
  lang,
  articleId,
  token,
}: {
  lang: Lang;
  articleId: string;
  token: string;
}): Promise<ArticleInfoResponse["data"]> => {
  if (!articleId || !token) throw new Error("missing params");

  const endpoint = `/article-info/${articleId}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: {
      "Accept-Language": lang,
      "X-User-Profile": token,
    },
  });
  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json()).data;
};

export const useArticleInfo = ({ articleId }: { articleId: string }) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useQuery(
    ["article-info", lang, backendToken, articleId],
    () => getArticleInfo({ articleId, lang, token: backendToken ?? "Guest" }),
    {
      enabled: !!articleId,
    }
  );
};
